import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import { isWeCom } from '@/utils/wecom';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { getLoginUrlApi, getUserInfoApi } from '@/api/auth';
import { WECOM_CORP_ID } from '@/config/env';
import { toast } from 'sonner';
import { initWecom } from '@/utils/wecom';
interface AuthGuardProps {
  children: React.ReactNode;
  setUserInfo: (userInfo: any) => void;
  userInfo: any;
}

import { routeTitles } from '@/App';
const AuthGuard: React.FC<AuthGuardProps> = ({ children, setUserInfo, userInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [contentMessage, setContentMessage] = useState('正在检查认证状态...');
  const hasRedirected = useRef(false); // 防止重复跳转
  const isRedirecting = useRef(false); // 防止重复处理重定向
  const [isInitialized, setIsInitialized] = useState(false); // 是否已初始化
  const [error, setError] = useState<{
    message: string;
    type: 'AUTH_FAILED' | 'LOGIN_FAILED' | 'WECOM_INIT_FAILED' | 'NETWORK_ERROR';
  } | null>(null);

  // 需要认证的路径
  const protectedPaths = Object.keys(routeTitles).filter(key => key !== '*');

  const [currentHost, setCurrentHost] = useState(window.location.host);

  // 认证相关路径
  const authPaths = ['/auth/wecom-check'];

  // 刷新认证
  const retryAuth = () => {
    console.log('重新开始认证...');
    setError(null);
    setIsChecking(true);
    setContentMessage('正在重新检查认证状态...');
    setIsInitialized(false);
    hasRedirected.current = false;
    isRedirecting.current = false;

    // 重新触发认证检查
    window.location.reload();
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // 如果已经跳转过或正在处理重定向，不再执行
        if (hasRedirected.current || isRedirecting.current) {
          return;
        }

        // 如果当前在认证相关页面，直接显示
        if (authPaths.includes(location.pathname)) {
          setIsChecking(false);
          setIsInitialized(true);
          return;
        }

        // 如果当前不在需要保护的路径，直接显示
        if (!protectedPaths.includes(location.pathname)) {
          setIsChecking(false);
          setIsInitialized(true);
          return;
        }

        // 检查是否在企微环境
        // if (!isWeCom) {
        //   console.log('不在企微环境，跳转到企微检测页面');
        //   navigate('/auth/wecom-check', { replace: true });
        //   return;
        // }

        // 检查URL中是否有code参数（企微授权回调）
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        console.log('code', code);
        if (code) {
          // 有code参数，说明是企微授权回调，需要处理登录
          console.log('检测到企微授权回调，开始处理登录...');
          await handleWecomCallback(code);
          return;
        }

        // 检查是否已经登录（有本地存储的用户信息）
        // const userInfo = localStorage.getItem('user_info');

        if (userInfo) {
          // 已登录，显示内容
          console.log('用户已登录，显示内容');
          setIsChecking(false);
          setIsInitialized(true);
          return;
        }

        // 未登录且没有code参数，获取登录链接
        console.log('用户未登录，获取企微登录链接...');
        setContentMessage('正在获取企微登录链接...');
        isRedirecting.current = true; // 标记正在处理重定向

        try {
          const res = await getLoginUrlApi({
            corpId: WECOM_CORP_ID,
            redirectUri: window.location.origin
          });

          setCurrentHost(window.location.origin);

          console.log('获取登录链接成功:', res);
          hasRedirected.current = true; // 标记已跳转
          setContentMessage('正在跳转到企微授权页面...');

          // 使用 setTimeout 确保状态更新后再跳转
          setTimeout(() => {
            window.location.href = res;
          }, 100);
        } catch (err) {
          console.error('获取登录链接失败:', err);
          isRedirecting.current = false; // 重置重定向状态
          toast.error(err || '获取登录链接失败');
          setError({
            message: '获取登录链接失败',
            type: 'NETWORK_ERROR'
          });
          setIsChecking(false);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('认证检查失败:', error);
        isRedirecting.current = false; // 重置重定向状态
        setError({
          message: '认证检查失败',
          type: 'AUTH_FAILED'
        });
        setIsChecking(false);
        setIsInitialized(true);
      }
    };

    checkAuth();
  }, [navigate, location.pathname]);

  // 监听路由变化，立即显示加载状态
  useEffect(() => {
    if (!isInitialized) {
      setIsChecking(true);
      setContentMessage('正在检查认证状态...');
    }
  }, [location.pathname, isInitialized]);

  // 处理企微授权回调
  const handleWecomCallback = async (code: string) => {
    try {
      isRedirecting.current = true;
      console.log('开始处理企微授权回调...');
      setContentMessage('开始获取用户信息...');

      // 步骤1: 获取用户信息
      console.log('获取用户信息...');
      const resInfoData = await getUserInfoApi({
        code,
        corpId: WECOM_CORP_ID
      });
      const resInfo = resInfoData.data;

      if (resInfo.code === '000000') {
        const userInfo = {
          userName: resInfo.data,
          traceId: resInfo.traceId
        };
        toast.success(`用户信息获取成功: ${resInfo.data}`);
        // 步骤3: 存储用户信息和认证信息
        setUserInfo(userInfo);
        // 步骤4: 初始化企微
        setContentMessage('开始初始化企微...');
        // const success = await initWecom({
        //   corpId: WECOM_CORP_ID,
        //   url: `${currentHost}`
        // });

        // if (!success) {
        //   setContentMessage('企微初始化失败');
        //   setError({
        //     message: '企微初始化失败',
        //     type: 'WECOM_INIT_FAILED'
        //   });
        //   setIsChecking(false);
        //   setIsInitialized(true);
        //   return;
        // }

        console.log('企微初始化成功');
        isRedirecting.current = false;

        // 步骤5: 清除URL中的code参数并刷新页面
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);

        // 步骤6: 直接进入首页
        navigate('/');
        setIsChecking(false);
        setIsInitialized(true);
      } else {
        toast.error(resInfo.message || '用户信息获取失败');
        setError({
          message: '用户信息获取失败',
          type: 'LOGIN_FAILED'
        });
        setIsChecking(false);
        setIsInitialized(true);
        return;
      }
    } catch (error) {
      console.error('处理企微授权回调失败:', error);
      isRedirecting.current = false;
      toast.error('登录失败: ' + (error instanceof Error ? error.message : '未知错误'));
      setError({
        message: '登录失败',
        type: 'LOGIN_FAILED'
      });
      setIsChecking(false);
      setIsInitialized(true);
    }
  };

  // 显示错误页面
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <div className="w-full max-w-md">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            {/* 错误图标和标题 */}
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">认证失败</h2>
              <p className="text-gray-600">{error.message}</p>
            </div>

            {/* 操作按钮 */}
            <div className="mt-6 flex items-center justify-center text-center">
              <button
                onClick={retryAuth}
                className="flex items-center justify-center gap-2 rounded-md bg-blue-500 px-6 py-3 text-white transition-colors hover:bg-blue-600"
              >
                <RefreshCw className="h-4 w-4" />
                重新认证
              </button>
            </div>

            {/* 帮助信息 */}
            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-center text-xs text-gray-600">如果问题持续存在，请联系技术支持</p>
              <p className="mt-1 text-center text-xs text-gray-500">
                错误时间: {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 显示加载状态
  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">{contentMessage}</p>
        </div>
      </div>
    );
  }

  // 如果正在重定向，也显示加载状态
  if (isRedirecting.current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">正在跳转到企微授权页面...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
